import React, { FC, useCallback, useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from 'utils';
import { PrimaryButton } from 'components/theme/Button/Button';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { filterAvailableSessions, filterSessionsByDate } from 'utils/session';
import { Session } from 'types/listings';

export enum BuyTicketButtonState {
  AVAILABLE = 'available',
  SOLD_OUT = 'sold_out',
  PASSED = 'passed',
  WAITLIST = 'waitlist',
}

interface Props {
  className?: string;
  date: string;
  state?: BuyTicketButtonState;
  dataTestId?: string;
  customLabel?: string;
  isFree?: boolean;
  disabled?: boolean;
}

const BuyTicketButton: FC<Props> = ({
  className,
  date,
  state = BuyTicketButtonState.AVAILABLE,
  dataTestId,
  customLabel,
  isFree = false,
  disabled = false,
}) => {
  const { t: translate } = useTranslation();
  const { listing, setEventBookingSessionParams } = useEventBookingSession();

  const label = useMemo(() => {
    if (customLabel) {
      return customLabel;
    }

    if (state === BuyTicketButtonState.AVAILABLE) {
      if (listing?.customCallToAction) {
        return listing.customCallToAction;
      }
      return isFree ? translate('rsvp') : translate('buyTickets');
    }
    if (state === BuyTicketButtonState.PASSED) {
      return translate('salesEnded');
    }
    if (state === BuyTicketButtonState.WAITLIST) {
      return translate('joinWaitlist');
    }
    return translate('soldOut');
  }, [state, customLabel]);

  const handleClick = useCallback(() => {
    if (
      listing &&
      (state === BuyTicketButtonState.AVAILABLE ||
        state === BuyTicketButtonState.WAITLIST)
    ) {
      // A single push: setEventBookingSessionParams already navigates to
      // `/event/<id>/tickets/<date>` (forceHistoryPush). The earlier extra bare
      // history.push to the same route created a second history entry, so the
      // in-app back button only undid one and appeared to do nothing.
      setEventBookingSessionParams(
        {
          date,
        },
        true,
      );
    }
  }, [listing, date, state, setEventBookingSessionParams]);

  return (
    <PrimaryButton
      className={className}
      data-testid={dataTestId}
      disabled={
        disabled ||
        (state !== BuyTicketButtonState.AVAILABLE &&
          state !== BuyTicketButtonState.WAITLIST)
      }
      onClick={handleClick}
    >
      {label}
    </PrimaryButton>
  );
};

export const getDateStateFromSessionsAvailability = (
  sessions: Session[],
  date: string,
  waitlistActive: boolean,
) => {
  const sessionsOnDate = filterSessionsByDate(sessions, date);

  const availableSessionsOnDate = filterAvailableSessions(sessionsOnDate);

  if (availableSessionsOnDate.length > 0) {
    return BuyTicketButtonState.AVAILABLE;
  }

  if (sessionsOnDate.length === 0) {
    return BuyTicketButtonState.PASSED;
  }

  if (waitlistActive && moment().format(DATE_FORMAT) <= date) {
    return BuyTicketButtonState.WAITLIST;
  }

  return BuyTicketButtonState.SOLD_OUT;
};

export default BuyTicketButton;
